/**
 * Groove PDF Page 2 Component
 * 
 * Renders the second page of WPS PDF for Groove welding type.
 * Contains:
 * - WPS record number, date, revision and company information
 * - Joint section with production drawings (if applicable)
 * - Post Weld Heat Treatment (PWHT) details with temperature, time, rates
 * - ISR (Intermediate Stress Relief) parameters for non-Overlay types
 * - DHT (Delayed Heat Treatment) information
 * - Welding Technique table (QW-410)
 * - Notes section with multi-line support
 * - Signature section with prepared/approved signatures
 * 
 * Includes responsive height adjustment and proper page footer
 */

import React, { useMemo } from "react";
import "../Css/Download_pdf.css";
import Groove_pdf_header from "./Groove_pdf_header_common";
import PageFooter from "./PageFooter";
import { TechniqueTable } from "./common";

import WPS_images from "./WPS_image";

const Groove_pdf_page2 = React.memo(({
  pdf,
  previewuser,
  previewapproved,
  Band,
  previewUrl1,
  previewUrl2,
  previewUrl3,
}) => {
  const pdfData = pdf[0];

  const { formattedDate, images, type } = useMemo(() => {
    const currentDate = new Date(pdfData?.WPSH_WPS_Date);
    const day = currentDate.getDate();
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    const formatted = isNaN(day) ? "" : `${day}-${month}-${year}`;
    
    return {
      formattedDate: formatted,
      images: [previewUrl1, previewUrl2, previewUrl3],
      type: [{ Type: pdfData?.wld_WPSType }]
    };
  }, [pdfData?.WPSH_WPS_Date, pdfData?.wld_WPSType, previewUrl1, previewUrl2, previewUrl3]);

  const styles = useMemo(() => ({
    width: "72.6%",
    height: window.innerHeight < 595 ? `${595 - window.innerHeight}px` : undefined
  }), []);  return (
    <div className="WPSMainPDF">
      <div id="container" className="WPSflexb">
        <div className="WPSheaderRow">
          {styles.height && <div style={{ height: styles.height }}></div>}
          <Groove_pdf_header from="WPS" pdf={pdf} />
        </div>
        
        {/* Table 4 */}
        <div id="body" className="WPSContent" style={{ height: "670px" }}>
          <br />
          
          <table id="table" className="WPSTable1">
            <tbody>
              <tr>
                <th
                  style={{
                    width: "13%",
                    borderTop: "1px solid",
                    borderBottom: "1px solid",
                    borderRight: "1px solid",
                    borderLeft: "1px solid",
                  }}
                >
                  WPS record number <br /> Date
                </th>
                <td
                  style={{
                    width: "25%",
                    borderTop: "1px solid",
                    borderBottom: "1px solid",
                  }}
                >
                  <span className="WPSforfont" style={{ float: "left" }}>
                    {pdf[0]?.WPSH_WPS_No}
                  </span>
                  <span className="WPSforfont" style={{ float: "right" }}>
                    Revision: {pdf[0]?.WPSH_WPS_Revi_No}
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </span>
                  <br />
                  <span className="WPSforfont" style={{ float: "left" }}>
                    {formattedDate === "NaN-NaN-NaN" ? "" : formattedDate}
                  </span>
                </td>
                <th
                  className="WPSright-th"
                  style={{
                    width: "15%",
                    borderLeft: "1px solid",
                    borderBottom: "1px solid",
                    borderTop: "1px solid",
                    borderRight: "1px solid",
                  }}
                >
                  Qualified to <br /> Company Name
                </th>
                <td
                  style={{
                    width: "35%",
                    borderTop: "1px solid",
                    borderBottom: "1px solid",
                    borderRight: "1px solid",
                  }}
                >
                  {pdf[0]?.WPS_QualifiedTo} <br /> {pdf[0]?.WPSH_CompanyName}
                </td>
              </tr>
            </tbody>
          </table>
          
          {pdf[0]?.wld_Joints_Type === "Joint section" && (
            <>
              <div className="WPSgroup">
                <div className="WPSbasemetal" style={{ width: "70%" }}>
                  JOINTS ( QW-402) Typical joints(s). See actual production drawings and engineering specification for details
                </div>
              </div>
              <WPS_images type={[type]} images={images} pdf={pdf} />
            </>
          )}
          
          <div className="WPSgroup">
            <div className="WPSbasemetal" style={{ width: "100%" }}>
              POST WELD HEAT TREATMENT (QW-407)
            </div>
          </div>
          
          <table id="table" className="WPSTable8" style={{ width: styles.width }}>
            <tr>
              <th
                style={{
                  width: "10.5%",
                  borderBottom: "0",
                  borderTop: "1px solid black",
                }}
              >
                Temperature
                <div className="WPSrcorner">°C</div>
              </th>
              <td
                style={{
                  width: "19%",
                  borderBottom: "0",
                  borderLeft: "none",
                  borderTop: "1px solid black",
                }}
              >
                {pdf[0]?.WPSH_PWHeatTre_Temperature}
              </td>
              <th
                style={{
                  width: "10%",
                  borderBottom: "0",
                  borderTop: "1px solid black",
                }}
              >
                Time
                <div className="WPSrcorner">Minutes</div>
              </th>
              <td
                style={{
                  width: "13%",
                  borderBottom: "0",
                  borderLeft: "none",
                  borderTop: "1px solid black",
                }}
              >
                {pdf[0]?.WPSH_PWHeatTre_Time}
              </td>
              <th
                rowSpan="3"
                style={{
                  width: "3%",
                  borderBottom: "0",
                  borderLeft: "1px solid black",
                  borderTop: "1px solid black",
                }}
              >
                Type
              </th>
              <td
                rowSpan="3"
                style={{
                  width: "15%",
                  borderBottom: "0",
                  borderLeft: "none",
                  borderTop: "1px solid black",
                  borderRight: "1px solid black",
                }}
              >
                {pdf[0]?.WPSH_PWHeatTre_Type}
              </td>
            </tr>
            
            <tr>
              <th style={{ width: "25%", borderBottom: "0", borderTop: "0" }}>
                Heating Rate
                <div className="WPSrcorner">°C/hr</div>
              </th>
              <td
                style={{
                  width: "15%",
                  borderBottom: "0",
                  borderTop: "0",
                  borderLeft: "none",
                }}
              >
                {pdf[0]?.WPSH_PWHeatTre_Heating_Rate}
              </td>
              <th style={{ width: "15%", borderBottom: "0", borderTop: "0" }}>
                Loading Temp.
                <div className="WPSrcorner">°C</div>
              </th>
              <td
                style={{
                  width: "25%",
                  borderBottom: "0",
                  borderTop: "0",
                  borderLeft: "none",
                }}
              >
                {pdf[0]?.WPSH_PWHeatTre_Heating_Method}
              </td>
            </tr>
            
            <tr>
              <th style={{ width: "25%", borderTop: "0", borderBottom: "0" }}>
                Cooling Rate
                <div className="WPSrcorner">°C/hr</div>
              </th>
              <td
                style={{
                  width: "15%",
                  borderTop: "0",
                  borderBottom: "0",
                  borderLeft: "none",
                }}
              >
                {pdf[0]?.WPSH_PWHeatTre_Cooling_Rate}
              </td>
              <th style={{ width: "15%", borderTop: "0", borderBottom: "0" }}>
                Unloading Temp.
                <div className="WPSrcorner">°C</div>
              </th>
              <td
                style={{
                  width: "25%",
                  borderTop: "0",
                  borderBottom: "0",
                  borderLeft: "none",
                }}
              >
                {pdf[0]?.WPSH_PWHeatTre_Cooling_Method}
              </td>
            </tr>
            
            <tr style={{ borderTop: "1px" }}>
              <th
                style={{
                  borderTop: "1px solid black",
                  borderBottom: "1px solid black",
                }}
              >
                PWHT Notes
              </th>
              <td
                colSpan="5"
                style={{
                  borderLeft: "none",
                  borderTop: "1px solid black",
                  borderBottom: "1px solid black",
                  borderRight: "1px solid black",
                }}
              >
                {pdf[0]?.WPSH_PWHeatTre_Notes != undefined
                  ? pdf[0]?.WPSH_PWHeatTre_Notes.toString().replace("\n", "<BR/>")
                  : pdf[0]?.WPSH_PWHeatTre_Notes}
              </td>
            </tr>
            
            {pdf[0]?.wld_WPSType !== "Overlay" && (
              <tr style={{ borderTop: "none", borderBottom: "1px solid black" }}>
                <th style={{ width: "25%", borderTop: "0" }}>
                  ISR Temprature
                  <div className="WPSrcorner">°C/hr</div>
                </th>
                <td
                  style={{
                    width: "15%",
                    borderTop: "0",
                    borderBottom: "0",
                  }}
                >
                  {pdf[0]?.WPSH_PWHeatTre_ISR_Temp}
                </td>
                <th
                  style={{
                    width: "20%",
                    borderTop: "0",
                    borderBottom: "1px solid black",
                  }}
                >
                  ISR Time
                  <div className="WPSrcorner">Minutes</div>
                </th>
                <td
                  colSpan="3"
                  style={{
                    width: "25%",
                    borderTop: "0",
                    borderBottom: "0",
                    borderLeft: "none",
                    borderRight: "1px solid black",
                  }}
                >
                  {pdf[0]?.WPSH_PWHeatTre_ISR_Time}
                </td>
              </tr>
            )}
            
            {pdf[0]?.wld_WPSType !== "Overlay" && (
              <tr>
                <th>ISR Notes</th>
                <td
                  colSpan="5"
                  style={{
                    borderLeft: "none",
                    borderRight: "1px solid black",
                    borderBottom: "1px solid black",
                  }}
                >
                  {pdf[0]?.WPSH_PWHeatTre_Weld_Notes}
                </td>
              </tr>
            )}
          </table>
          
          <table className="WPSTable6" style={{ width: "570px" }}>
            <tr>
              <td style={{ width: "60%" }}>
                &nbsp; DHT after completion of welding before cooling from Preheat prior to PWHT (QW-406.2)
              </td>
              <th
                style={{
                  width: "14%",
                  borderLeft: ".1px solid black",
                  borderRight: ".1px solid black",
                  borderBottom: ".1px solid black",
                }}
              >
                Temperature
                <div className="WPSrcorner">°C</div>
              </th>
              <td
                style={{
                  borderLeft: "none",
                  borderRight: ".1px solid black",
                  borderBottom: ".1px solid black",
                }}
              >
                {pdf[0]?.WPSH_PWHeatTre_Post_Heat_temp == undefined
                  ? "-"
                  : pdf[0]?.WPSH_PWHeatTre_Post_Heat_temp}{" "}
                °C for{" "}
                {pdf[0]?.WPSH_PWHeatTre_Post_Heat_time == undefined
                  ? "-"
                  : pdf[0]?.WPSH_PWHeatTre_Post_Heat_time}{" "}
                Minutes
              </td>
            </tr>
          </table>
          
          <br />
          
          <div className="WPSgroup">
            <div className="WPSbasemetal">TECHNIQUE (QW-410)</div>
          </div>
          
          <TechniqueTable pdf={pdf} type={pdfData?.wld_WPSType} />
          
          <div className="WPSgroup">
            <div className="WPSbasemetal">NOTES</div>
          </div>
          
          <table id="table" className="WPSTable10">
            <tbody>
              <tr>
                <td className="Noteswrap">
                  {pdf[0]?.WPSH_Notes != undefined
                    ? pdf[0]?.WPSH_Notes.split("\n").map((i) => {
                        return (
                          <>
                            {i}
                            <br />
                          </>
                        );
                      })
                    : ""}
                </td>
              </tr>
            </tbody>
          </table>
          
          <br />
          <br />
          
          <div style={{ position: "fixed", top: "1590px" }}>
            <PageFooter
              pdf={pdf}
              previewuser={previewuser}
              Band={Band}
              previewapproved={previewapproved}
            />
          </div>
        </div>
        
        <div style={{ position: "fixed", top: "1680px" }}>
          <table className="WPSfooter">
            <tr>
              <td>Page 2 of 2</td>
            </tr>
          </table>
        </div>
      </div>
    </div>
  );

});

export default Groove_pdf_page2; 
